import IconButton from "components/atoms/button/IconButton";
import Form from "components/atoms/form/Form";
import Input from "components/atoms/input/Input";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Icon from "components/atoms/icon/Icon";
import { IconType } from "lib/helpers/constants/iconTypes";
import InputsFolder from "components/molecules/inputs-folder/InputsFolder";
import PrimaryDatePicker from "components/molecules/datepicker/PrimaryDatePicker";
import Label from "components/atoms/input/input-label/InputLabel";
import CounterControl from "components/molecules/counter-control-append/CounterControlAppend";
import CreatableSelect from "react-select/creatable";
import { customStyles } from "lib/helpers/configs/customStyles";
import { FC, Fragment } from "react";
import { format } from "date-fns";
import { useShowPassowrd } from "lib/hooks/shared/useShowPasswod";
import Tooltip from "components/molecules/tooltip/Tooltip";
import { useHover } from "lib/hooks/shared/useHover";
import Button from "components/atoms/button/Button";
import { languageData } from "lib/helpers/constants/languageData";
import RadioButton from "components/atoms/radio-button/RadioButton";
import { nationalities } from "lib/helpers/constants/nationalities";
import { useUpdateEmployee } from "lib/hooks/mutations/Employee/useUpdateEmployee";
import { usePermissions } from "lib/hooks/shared/usePermissions";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";

export interface INewEmployeeStepsProps {
  setCurrentStep: (step: number) => void;
  setAllData: (data: any) => void;
  AllData: any;
  isUpdating?: boolean;
  onConfirm?: () => void;
}

const Step1EmployeeInfo: FC<INewEmployeeStepsProps> = ({
  setCurrentStep,
  setAllData,
  AllData,
  isUpdating = false,
  onConfirm
}) => {
  const { t } = useTranslation();
  const hasPermissionToViewAllEmployees = usePermissions(['employee_management_view'])
  const { showPassword, toggleShowPassword } = useShowPassowrd();
  const { isHovered, onMouseEnter, onMouseLeave } = useHover();
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      name: AllData?.name || "",
      last_name: AllData?.last_name || "",
      address: AllData?.address || "",
      zip_code: AllData?.zip_code || "",
      nationality: AllData?.nationality || "",
      language: AllData?.language || "",
      children: AllData?.children || "yes",
      marital_status: AllData?.marital_status || "",
      finma_number: AllData?.finma_number || "",
      bank_name: AllData?.bank_name || "",
      bank_number: AllData?.bank_number || "",
      betreibung: AllData?.betreibung || "",
      employe_number: AllData?.employe_number || "",
      ahv_number: AllData?.ahv_number || "",
      phone: AllData?.phone || "",
      password: AllData?.password || "",
      email: AllData?.email || "",
      gender: AllData?.gender || null,
      position: AllData?.position || "",
      team: AllData?.team || "",
      department: AllData?.department || "",
      weekly_hours: AllData?.weekly_hours || 0,
      office_location: AllData?.office_location || "",
      supervisor: AllData?.supervisor || "",
      hire_date: AllData?.hire_date || format(new Date(), "yyyy-MM-dd"),
      birthdate: AllData?.birthdate || null,
      length_of_probation: AllData?.length_of_probation || "",
      contract_ends: AllData?.contract_ends || null,
      vacation_days: AllData?.vacation_days || 0,
      on_boarding_template_id: AllData?.on_boarding_template_id || null,
    },
  });

  const onSubmit = (data: any) => {
    setCurrentStep(2);
    setAllData({ ...AllData, ...data });
  };

  const {
    mutateAsync: updateEmployee,
    isLoading: updateLoading,
  } = useUpdateEmployee();


  const updateStep = () => {
    updateEmployee({ ...AllData, ...watch() })
    if(!hasPermissionToViewAllEmployees){
      onConfirm?.()
    }

  }



  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-0 flex flex-col gap-5">
        <InputsFolder title={t("Public Profile")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              label={t("First Name")}
              placeholder={t("Please enter first name")}
              {...register("name", {
                required: "This field is required",
              })}
              error={errors?.name}
            />
            <Input
              label={t("Last Name")}
              placeholder={t("Please enter last name")}
              {...register("last_name", { required: "This field is required" })}
              error={errors?.last_name}
            />
            <div className="relative">
              <Label text={t("Gender")} />
              <Controller
                name="gender"
                rules={{ required: "This field is required" }}
                control={control}
                render={({ field }) => (
                  <CreatableSelect<any, false>
                    {...field}
                    placeholder={t("Choose gender")}
                    options={[
                      { label: t("Male"), value: "Male" },
                      { label: t("Female"), value: "Female" },
                    ]}
                    value={
                      field?.value
                        ? {
                          value: field?.value,
                          label: t(field?.value),
                        }
                        : null
                    }
                    className="cursor-pointer font-inter-regular react-select-container"
                    classNamePrefix="react-select"
                    onChange={(newValue: any, actionMeta: any) => {
                      if (
                        actionMeta.action === "select-option" &&
                        newValue?.value
                      ) {
                        field.onChange(newValue.value);
                      }
                    }}
                    styles={customStyles}
                    isValidNewOption={() => false}
                    menuPosition={"fixed"}
                    menuPortalTarget={document.body}
                  />
                )}
              />
              {errors.gender?.message && (
                <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                  {errors.gender?.message as string}
                </div>
              )}
            </div>
            <Input
              label={t("Email")}
              placeholder={t("Enter email")}
              type="email"
              {...register("email", { required: "This field is required" })}
              error={errors?.email}
              disabled={isUpdating}
            />
            <Input
              label={t("Phone Number")}
              placeholder={t("Please enter Phone number")}
              {...register("phone", { required: "This field is required" })}
              error={errors?.phone}
              type="tel"
            />
            {!isUpdating && (
              <Input
                label="Passwort"
                placeholder="••••••••"
                type={showPassword ? "text" : "password"}
                {...register("password",
                  {
                    required: "This field is required",
                    minLength: { value: 8, message: "Password must be at least 8 characters" }
                  })}
                error={errors?.password}
                icon={
                  showPassword
                    ? IconType.HIDDEN_PASSWORD_ICON
                    : IconType.SHOW_PASSWORD_ICON
                }
                toggleShowPassowrd={toggleShowPassword}
              />
            )}
          </div>
        </InputsFolder>
        <InputsFolder title={t("Personal Information")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Controller
              control={control}
              name="birthdate"
              rules={{ required: `${t("This field is required")}` }}
              render={({ field: { onChange, value } }) => (
                <PrimaryDatePicker
                  label={t("Birthdate")}
                  startDate={value}
                  setStartDate={(date: string) => onChange(date)}
                  error={errors?.birthdate}
                />
              )}
            />
            <Input
              label={t("Address")}
              placeholder={t("Please enter address")}
              {...register("address", {
                required: "This field is required",
              })}
              error={errors?.address}
            />
            <Input
              label={t("Postal Code")}
              placeholder={t("Please enter postal code")}
              {...register("zip_code", { required: "This field is required" })}
              error={errors?.zip_code}
            />
            <div className="relative">
              <Label text={t('Nationality')} />
              <Controller
                name="nationality"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <CreatableSelect<any, false>
                    {...field}
                    placeholder={t('Nationality')}
                    options={nationalities.map(nationality => {
                      return {
                        label: t(nationality), value: nationality
                      }
                    })}

                    value={
                      field.value
                        ? { value: field.value, label: t(field.value) }
                        : null
                    }
                    className="cursor-pointer font-inter-regular react-select-container"
                    classNamePrefix="react-select"
                    onChange={(
                      newValue: any,
                      actionMeta: any
                    ) => {
                      if (
                        actionMeta.action === "select-option" &&
                        newValue?.value
                      ) {
                        field.onChange(newValue.value);
                      }
                    }}
                    styles={customStyles}
                    isValidNewOption={() => false}
                    menuPosition={"fixed"}
                    menuPortalTarget={document.body}

                  />
                )}
              />
              {errors.nationality?.message && (
                <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                  <>{t(errors.nationality?.message as string)}</>
                </div>
              )}
            </div>
            <div className="relative">
              <Label text={t("Language")} />
              <Controller
                name="language"
                rules={{ required: "This field is required" }}
                control={control}
                render={({ field }) => (
                  <CreatableSelect<any, false>
                    {...field}
                    placeholder={t("Choose language")}
                    options={languageData.map((language) => ({
                      value: language.value,
                      label: t(language.label),
                    }))}
                    value={
                      field.value
                        ? { value: field.value, label: t(languageData.find((lang) => lang.value === field.value)?.label as string) }
                        : null
                    }
                    className="cursor-pointer font-inter-regular react-select-container"
                    classNamePrefix="react-select"
                    onChange={(newValue: any, actionMeta: any) => {
                      if (
                        actionMeta.action === "select-option" &&
                        newValue?.value
                      ) {
                        field.onChange(newValue.value);
                      }
                    }}
                    styles={customStyles}
                    isValidNewOption={() => false}
                    menuPosition={"fixed"}
                    menuPortalTarget={document.body}
                  />
                )}
              />
              {errors.language?.message && (
                <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                  {errors.language?.message as string}
                </div>
              )}
            </div>
            <div className="relative">
              <Label text={t("Marital Status")} />
              <Controller
                name="marital_status"
                rules={{ required: "This field is required" }}
                control={control}
                render={({ field }) => (
                  <CreatableSelect<any, false>
                    {...field}
                    placeholder={t("Choose marital status")}
                    options={[
                      { label: t("Single"), value: "Single" },
                      { label: t("Married"), value: "Married" },
                      { label: t("Divorced"), value: "Divorced" },
                      { label: t("Widowed"), value: "Widowed" },
                    ]}
                    value={
                      field.value
                        ? { value: field.value, label: t(field.value) }
                        : null
                    }
                    className="cursor-pointer font-inter-regular react-select-container"
                    classNamePrefix="react-select"
                    onChange={(newValue: any, actionMeta: any) => {
                      if (
                        actionMeta.action === "select-option" &&
                        newValue?.value
                      ) {
                        field.onChange(newValue.value);
                      }
                    }}
                    styles={customStyles}
                    isValidNewOption={() => false}
                    menuPosition={"fixed"}
                    menuPortalTarget={document.body}
                  />
                )}
              />
              {errors.marital_status?.message && (
                <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                  {errors.marital_status?.message as string}
                </div>
              )}
            </div>
            <div>
              <Label>{t("Children")}</Label>
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  name="children"
                  control={control}
                  render={({ field }) => (
                    <RadioButton
                      label={t("Yes")}
                      isChecked={field.value === "yes"}
                      value={"yes"}
                      name={"children"}
                      onChange={() => field.onChange("yes")}
                      labelClassName="h-[41px] 2xl:h-[44px] items-center flex bg-white"
                      iconWrapperClassName="!top-[20px] 2xl:!top-[21px] !right-[6px]"
                      className="right-[8px]"
                    />
                  )}
                />
                <Controller
                  name="children"
                  control={control}
                  render={({ field }) => (
                    <RadioButton
                      label={t("No")}
                      isChecked={field.value === "no"}
                      value={"no"}
                      name={"children"}
                      onChange={() => field.onChange("no")}
                      labelClassName="h-[41px] 2xl:h-[44px] items-center flex bg-white"
                      iconWrapperClassName="!top-[20px] 2xl:!top-[21px] !right-[6px]"
                      className="right-[8px]"
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <Label>{t("Betreibung")}</Label>
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  name="betreibung"
                  control={control}
                  render={({ field }) => (
                    <RadioButton
                      label={t("Yes")}
                      isChecked={field.value === "yes"}
                      value={"yes"}
                      name={"betreibung"}
                      onChange={() => field.onChange("yes")}
                      labelClassName="h-[41px] 2xl:h-[44px] items-center flex bg-white"
                      iconWrapperClassName="!top-[20px] 2xl:!top-[21px] !right-[6px]"
                      className="right-[8px]"
                    />
                  )}
                />
                <Controller
                  name="betreibung"
                  control={control}
                  render={({ field }) => (
                    <RadioButton
                      label={t("No")}
                      isChecked={field.value === "no"}
                      value={"no"}
                      name={"betreibung"}
                      onChange={() => field.onChange("no")}
                      labelClassName="h-[41px] 2xl:h-[44px] items-center flex bg-white"
                      iconWrapperClassName="!top-[20px] 2xl:!top-[21px] !right-[6px]"
                      className="right-[8px]"
                    />
                  )}
                />
              </div>
            </div>
            <Input
              label={t("Finma Number")}
              placeholder={t("Please enter finma number")}
              {...register("finma_number", { required: "This field is required" })}
              error={errors?.finma_number}
            />
            <Input
              label={t("Employee Number")}
              placeholder={t("Please enter employee number")}
              {...register("employe_number", { required: "This field is required" })}
              error={errors?.employe_number}
            />
            <Input
              label={t("AHV Number")}
              placeholder={t("Please enter AHV number")}
              {...register("ahv_number", { required: "This field is required" })}
              error={errors?.ahv_number}
            />
          </div>
        </InputsFolder>
        <InputsFolder title={t("Bank Information")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              label={t("Bank Name")}
              placeholder={t("Please enter bank name")}
              {...register("bank_name", {
                required: "This field is required",
              })}
              error={errors?.bank_name}
            />
            <Input
              label={t("Bank Number")}
              placeholder={t("Please enter bank number")}
              {...register("bank_number", { required: "This field is required" })}
              error={errors?.bank_number}
            />

          </div>
        </InputsFolder>
        <ConditionalRender condition={hasPermissionToViewAllEmployees}>
        <InputsFolder title={t("HR Information")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              label={t("Position")}
              placeholder={t("Enter position")}
              {...register("position", { required: "This field is required" })}
              error={errors?.position}
            />
            <Input
              label={t("Team")}
              placeholder={t("Choose team")}
              type="text"
              {...register("team")}
              error={errors?.team}
            />
            <Input
              label={t("Department")}
              placeholder={t("Choose department")}
              type="text"
              {...register("department")}
              error={errors?.department}
            />

            <div>
              <Label>{t("Weekly Hours")}</Label>
              <div className="w-full">
                <Controller
                  control={control}
                  name="weekly_hours"
                  rules={{ required: "This field is required" }}
                  render={({ field: { onChange, value } }) => (
                    <CounterControl
                      count={value}
                      className="!mt-0"
                      classes={{
                        container: "w-full bg-white !border-inputborder",
                      }}
                      isInput
                      onChange={(e: any) => {
                        onChange(parseFloat(e.target.value));
                      }}
                      onIncrement={() => onChange(value + 1)}
                      onDecrement={() =>
                        onChange(value >= 1 ? value - 1 : value)
                      }
                    />
                  )}
                />
              </div>
            </div>
            <Input
              label={t("Office")}
              placeholder={t("Choose office")}
              type="text"
              {...register("office_location", {
                required: "This field is required",
              })}
              error={errors?.office_location}
            />
            <Input
              label={t("Supervisor")}
              placeholder={t("Choose supervisor")}
              type="text"
              {...register("supervisor", {
                required: false
              })}
              error={errors?.supervisor}
            />
            {!isUpdating && (

              <Controller
                control={control}
                name="hire_date"
                rules={{ required: "Hire date is required" }}
                render={({ field: { onChange, value } }) => (
                  <PrimaryDatePicker
                    label={t("Hire Date")}
                    startDate={value}
                    {...register("hire_date")}
                    setStartDate={(date: string) => onChange(date)}
                    error={errors.hire_date}
                  />
                )}
              />
            )}
            <Input
              label={t("Length Of Probation")}
              placeholder={t("Choose length of probation")}
              type="number"
              {...register("length_of_probation")}
              error={errors?.length_of_probation}
            />
            {!isUpdating && (

              <div>
                <div className="flex items-center gap-2">
                  <Label>{t("Contract Ends")}</Label>
                  <div className="relative" onMouseLeave={onMouseLeave}>
                    <Button onMouseEnter={onMouseEnter}>
                      <Icon
                        iconType={IconType.ALERT_ICON}
                        className="absolute right-[-10] bottom-[5px]"
                        color={"var(--theme)"}
                      />
                    </Button>
                    <Tooltip
                      isOpen={isHovered}
                      className="absolute right-0 w-[250px] h-auto bg-white p-[10px] rounded-[25px] shadow-custom  top-[30px] left-[10px] !z-[99999]"
                      content={
                        <Fragment>
                          <ul className="list-disc text-[14px]  ml-4 font-inter-regular">
                            <li>
                              {t(
                                "Add only if its a fixed-term employment contract"
                              )}.
                            </li>
                          </ul>
                        </Fragment>
                      }
                    />
                  </div>
                </div>
                <Controller
                  control={control}
                  name="contract_ends"
                  render={({ field: { onChange, value } }) => (
                    <PrimaryDatePicker
                      // label={t("Contract Ends")}
                      startDate={value}
                      {...register("contract_ends")}
                      setStartDate={(date: string) => onChange(date)}
                      error={errors.contract_ends}
                    />
                  )}
                />
              </div>
            )}

            <div>
              <Label>{t("Vacation Days")}</Label>
              <div className="w-full">
                <Controller
                  control={control}
                  name="vacation_days"
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <CounterControl
                      count={value}
                      className="!mt-0"
                      classes={{
                        container: "w-full bg-white !border-inputborder",
                      }}
                      isInput
                      onChange={(e: any) => {
                        onChange(parseFloat(e.target.value));
                      }}
                      onIncrement={() => onChange(value + 1)}
                      onDecrement={() =>
                        onChange(value >= 1 ? value - 1 : value)
                      }
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </InputsFolder>
        </ConditionalRender>
        {/* <InputsFolder title={t('On-Boarding')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                        <div className="relative">
                            <Label text={t('Assign Template')} />
                            <Controller
                                name="on_boarding_template_id"
                                control={control}
                                render={({ field }) => (
                                    <CreatableSelect<any, false>
                                        {...field}
                                        placeholder={t('Choose template')}
                                        options={[
                                            // { label: t('template 1'), value: 'template 1' },
                                            // { label: t('template 2'), value: 'template 2' },
                                        ]}
                                        value={
                                            field?.value
                                                ? {
                                                    value: field?.value,
                                                    label: t(field?.value),
                                                }
                                                : null
                                        }
                                        className="cursor-pointer font-inter-regular react-select-container"
                                        classNamePrefix="react-select"
                                        onChange={(newValue: any, actionMeta: any) => {
                                            if (
                                                actionMeta.action === "select-option" &&
                                                newValue?.value
                                            ) {
                                                field.onChange(newValue.value);
                                            }
                                        }}
                                        styles={customStyles}
                                        isValidNewOption={() => false}
                                        menuPosition={"fixed"}
                                        menuPortalTarget={document.body}
                                    />
                                )}
                            />
                            {errors.on_boarding_template_id?.message && (
                                <>
                                    <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                                        <>{errors.on_boarding_template_id?.message}</>
                                    </div>
                                </>
                            )}
                        </div>

                    </div>
                </InputsFolder> */}
      </div>
      <div className="flex flex-row justify-end gap-5 pt-10">
        {isUpdating && (
          <IconButton className="" type="button" secondary disabled={updateLoading} onClick={updateStep}>
            {t(`Submit`)}
          </IconButton>
        )}
        
        {hasPermissionToViewAllEmployees && (
          <IconButton className="" type="submit">
            <div className="flex items-center gap-2">
              {t(`Next Step`)}
              <Icon iconType={IconType.RIGHT_ARROW_ICON} />
            </div>
          </IconButton>
        )}
      </div>
    </Form>
  );
};

export default Step1EmployeeInfo;
